import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ExpoOrnaments } from 'component/Ornaments';
import './Modal.scss';

const ModalContext = createContext(null);

export const ModalProvider = ({ children }) => {
  const [modals, setModals] = useState([]);

  const openModal = useCallback((content, options = {}) => {
    const id = Date.now() + Math.random();
    const modal = {
      id,
      content,
      options: {
        closeOnBackdrop: true,
        closeOnEscape: true,
        showCloseButton: true,
        size: 'medium', // 'small', 'medium', 'large', 'full'
        className: '',
        ...options
      }
    };

    setModals(prev => [...prev, modal]);
    return id;
  }, []);

  const closeModal = useCallback((id) => {
    setModals(prev => prev.filter(m => m.id !== id));
  }, []);

  const closeAll = useCallback(() => {
    setModals([]);
  }, []);

  const closeLastModal = useCallback(() => {
    setModals(prev => prev.slice(0, -1));
  }, []);

  return (
    <ModalContext.Provider value={{ openModal, closeModal, closeAll, closeLastModal }}>
      {children}
      {modals.map(modal => (
        <ModalPortal key={modal.id} modal={modal} onClose={closeModal} />
      ))}
    </ModalContext.Provider>
  );
};

const ModalPortal = ({ modal, onClose }) => {
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && modal.options.closeOnBackdrop) {
      onClose(modal.id);
    }
  };

  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape' && modal.options.closeOnEscape) {
      onClose(modal.id);
    }
  }, [modal.id, modal.options.closeOnEscape, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [handleEscape]);

  return createPortal(
    <div
      className={`modal-overlay ${modal.options.className}`}
      onClick={handleBackdropClick}
    >
      <div className={`modal-container modal-${modal.options.size}`}>
        <ExpoOrnaments>
          {modal.options.showCloseButton && (
            <button
              className="modal-close"
              onClick={() => onClose(modal.id)}
              aria-label="Close"
            >
              ×
            </button>
          )}
          <div className="modal-content">
            {React.isValidElement(modal.content)
              ? React.cloneElement(modal.content, {
                modalInstance: { close: () => onClose(modal.id), id: modal.id },
                close: () => onClose(modal.id),
                modalClassName: modal.options.className
              })
              : typeof modal.content === 'function'
                ? modal.content({
                  close: () => onClose(modal.id),
                  modalInstance: { close: () => onClose(modal.id), id: modal.id },
                  modalClassName: modal.options.className
                })
                : modal.content
            }
          </div>
        </ExpoOrnaments>
      </div>
    </div>,
    document.body
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within ModalProvider');
  }
  return context;
};

// Hook untuk komponen yang ingin mengakses modal instance
export const useModalInstance = () => {
  const [modalInstance, setModalInstance] = useState(null);
  const { openModal, closeModal } = useModal();

  const open = useCallback((content, options) => {
    const id = openModal(content, options);
    setModalInstance({ id, close: () => closeModal(id) });
    return id;
  }, [openModal, closeModal]);

  return { open, close: () => modalInstance?.close(), instance: modalInstance };
};
